import React, { createContext, useContext, useState } from 'react';

import { Toolbar } from '@devexpress/dx-react-grid-material-ui';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  Menu,
  MenuItem,
  Tooltip,
  CircularProgress
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { Send } from 'lucide-react';
import PropTypes from 'prop-types';
import AddIcon from '@material-ui/icons/Add';

const useStyles = makeStyles((theme) => ({
  root: {
    justifyContent: 'flex-start',
    gap: theme.spacing(0.5),
    flexWrap: 'wrap',
    '& > div:first-of-type': {
      display: 'none'
    },
    '& #add-task-btn': {
      marginRight: theme.spacing(1)
    }
  },
  sendButton: {
    color: 'rgba(0, 0, 0, 0.54)'
  }
}));

// Keep the grid toolbar component type stable while its actions and state change.
export const TaskToolbarContext = createContext({});
export const TaskToolbarRoot = (props) => {
  const options = useContext(TaskToolbarContext);
  return <CustomToolbarRoot {...props} {...options} />;
};

const CustomToolbarRoot = ({
  children,
  showSend = false,
  showAddTask = false,
  selectionCount = 0,
  isSending = false,
  isAddTaskDisabled = false,
  onAddTask,
  onSendAll,
  onSendSelected,
  ...restProps
}) => {
  const classes = useStyles();
  const [anchorEl, setAnchorEl] = useState(null);
  const [pendingSend, setPendingSend] = useState(null);

  const requestSendConfirmation = (callback) => {
    setAnchorEl(null);
    setPendingSend(() => callback);
  };

  const confirmSend = () => {
    const callback = pendingSend;
    setPendingSend(null);
    callback?.();
  };

  return (
    <Toolbar.Root {...restProps} className={classes.root}>
      {showAddTask && (
        <Button
          id="add-task-btn"
          variant="contained"
          color="primary"
          startIcon={isAddTaskDisabled ? <CircularProgress size={16} style={{ color: 'white' }} /> : <AddIcon />}
          onClick={onAddTask}
          disabled={isAddTaskDisabled}
        >
          Add Task
        </Button>
      )}

      {children}

      {showSend && (
        <>
          <Tooltip title="Send task reminder">
            <span>
              <IconButton
                aria-label="Send task reminder"
                aria-controls={anchorEl ? 'task-reminder-menu' : undefined}
                aria-haspopup="true"
                className={classes.sendButton}
                disabled={isSending}
                onClick={(event) => setAnchorEl(event.currentTarget)}
              >
                <Send size={24} />
              </IconButton>
            </span>
          </Tooltip>
          <Menu
            id="task-reminder-menu"
            anchorEl={anchorEl}
            keepMounted
            open={Boolean(anchorEl)}
            onClose={() => setAnchorEl(null)}
          >
            <MenuItem onClick={() => requestSendConfirmation(onSendAll)}>Send reminder to all</MenuItem>
            {selectionCount > 0 && (
              <MenuItem onClick={() => requestSendConfirmation(onSendSelected)}>Send reminder to selected</MenuItem>
            )}
          </Menu>
          <Dialog
            open={Boolean(pendingSend)}
            onClose={() => setPendingSend(null)}
            aria-labelledby="task-reminder-confirmation-title"
            aria-describedby="task-reminder-confirmation-description"
          >
            <DialogTitle id="task-reminder-confirmation-title">Send WhatsApp reminders?</DialogTitle>
            <DialogContent>
              <DialogContentText id="task-reminder-confirmation-description">
                Only rows whose status is “Assigned” will receive WhatsApp notifications.
              </DialogContentText>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setPendingSend(null)} color="default">
                Cancel
              </Button>
              <Button onClick={confirmSend} color="primary" variant="contained" autoFocus>
                Send reminders
              </Button>
            </DialogActions>
          </Dialog>
        </>
      )}
    </Toolbar.Root>
  );
};

export default CustomToolbarRoot;

CustomToolbarRoot.propTypes = {
  children: PropTypes.any,
  showSend: PropTypes.bool,
  showAddTask: PropTypes.bool,
  selectionCount: PropTypes.number,
  isSending: PropTypes.bool,
  isAddTaskDisabled: PropTypes.bool,
  onAddTask: PropTypes.func,
  onSendAll: PropTypes.func,
  onSendSelected: PropTypes.func
};
